import Cookies from '@comeon/tomcat-cookies';
import { insertOldPixel, registrationFailure, registrationSuccess, insertTrackEvent } from '@comeon/analytics';
import { isCasinoGamePath } from '~/utils/pathname';
import storage from '~/store/storage';
import { requestApi } from '~/services/api.js';
import {
	COOKIE_PLAYER_BALANCE,
	COOKIE_PLAYER_CASINO_NET_POSITION,
	COOKIE_PLAYER_POINTS,
	setCookie,
	clearCookie
} from '~/store/cookies.js';
import toLocation from '~/utils/login-to-location-utils.js';
import { ROUTE_ACCOUNT, ROUTE_PROMOTIONS, ROUTE_WE_SPIN } from '~/components/router/constants.js';
import { reopenAccountCall } from './reset-password/api/index';
import {
	getLocaleInfoCall,
	getTokenCall,
	loginCall,
	loginLugasCall,
	logoutCall,
	preloginCall,
	registerCall,
	registrationPhoneVerificationCall,
	registrationSMSResendCall,
	sendClientIdForTrackingCall,
	sendExtraDetailsCall,
	senStateCodeCall,
	setTermsAndConditionsCall,
	setPrivacyPolicyCall,
	updateMissingDataCall
} from './api/index';
import socialLoginCall from './social-media/api/index.js';
import { REGISTRATION_TYPES } from './ducks/constants';

const domain = window.location.hostname.replace('mobile.', '').replace('www', '');

function getGoogleClientId() {
	try {
		return window?.ga?.getAll ? window.ga.getAll()[0].get('clientId') : null;
	} catch (e) {
		return null;
	}
}

async function sendClientIdForTracking() {
	const payload = getGoogleClientId();
	if (payload) {
		await sendClientIdForTrackingCall(payload);
	}
}

/**
 * Inform BE that user had login succesfully
 * @returns {Promise}
 */
async function completeSession() {
	try {
		return await requestApi(`/auth/login/complete`, {}, 'POST');
	} catch {
		return {};
	}
}

function postAuthSuccess({
	isComingFromInit = false,
	isRegistration = false,
	callbackFunction = undefined,
	onSuccessCallback = () => {}
}) {
	Cookies.set('login', true); // used by pixeltracking (and others?)
	if (!isComingFromInit && !isRegistration) {
		insertOldPixel('login', callbackFunction);
	}

	if (!isComingFromInit) {
		completeSession(); // Register the session started in backend
	}

	sendClientIdForTracking();

	if (onSuccessCallback) {
		onSuccessCallback();
	}

	// Redirect to B2B brand after login
	const postAuthUrl = Cookies.get('postAuthUrl');

	if (!isRegistration && postAuthUrl) {
		window.open(postAuthUrl);
		Cookies.remove('postAuthUrl');
	}
}

function removeRealityCheckCookies() {
	clearCookie('RCCC');
	clearCookie('RCETU');
	clearCookie('RCAPCC');
	clearCookie('RCAPCCC');
	clearCookie('RCAID');
	clearCookie('RCID');
	clearCookie('RCAccepted');
}

function removeSessionTime() {
	Cookies.remove('loggedInTime', {
		domain
	});
	// Remove the cookies used in player info screen for Germany
	Cookies.remove('playerInfoLastShown', { domain });
	Cookies.remove('playerInfoAccepted', { domain });
	removeRealityCheckCookies();
}

function setSessionTime() {
	Cookies.remove('loggedInTime', { domain });
	removeSessionTime();
	Cookies.set('loggedInTime', Date.now(), {
		domain
	});
}

/**
 * @param {object} payload
 * @param {Boolean} social
 * @returns {Promise}
 */
async function login(payload, social, onSuccessCallback) {
	let response;

	if (social) {
		response = await socialLoginCall(payload);
	} else {
		response = await loginCall(payload);
	}
	// TODO try to remove
	if (
		response.status !== 'FAILURE' &&
		!response.result.shouldShowComplianceMessage &&
		!response.result.shouldShowPrivacyPolicyMessage &&
		!response.result.showDepositLimits &&
		!response.result.mfaEnabled &&
		!response.result.showPhoneVerification &&
		!response.result.mustChangePw &&
		!response.result.showPlayerDetailsPage &&
		!response.result.requiresLoginOnLugas &&
		!response.result.showSchufaVerification &&
		!response.result.shouldShowWagerAndTimeLimitPage &&
		!response.result.shouldShowDepositLimitPage &&
		!response.result.shouldShowBalanceLimitPage &&
		!response.result.shouldShowSessionLimitPage
	) {
		postAuthSuccess({ onSuccessCallback });
	}

	return response;
}

/* API to check whether the country from which user is trying to login in is same as user's registering country */
/* useful for captcha */
async function prelogin({ email }) {
	try {
		return await preloginCall(email);
	} catch (e) {
		return {
			status: 'FAILURE',
			errorCode: 'PRE_LOGIN_FAILURE',
			errorDescription: 'PRE_LOGIN_FAILURE'
		};
	}
}

async function setTermsAndConditions(payload) {
	try {
		return await setTermsAndConditionsCall(payload);
	} catch (e) {
		return {
			status: 'FAILURE',
			errorCode: 'TNC_UPDATE_FAILURE',
			errorDescription: 'TNC_UPDATE_FAILURE'
		};
	}
}

async function setPrivacyPolicy(payload) {
	try {
		return await setPrivacyPolicyCall(payload);
	} catch (e) {
		return {
			status: 'FAILURE',
			errorCode: 'PP_UPDATE_FAILURE',
			errorDescription: 'PP_UPDATE_FAILURE'
		};
	}
}
function postLogoutFail() {
	// console.log(
	//	`Failed to logout. ${errorDescription} Error code: ${errorCode}`
	// );
}

function postLogout() {
	Cookies.remove('playerIsGuest', { domain });
	Cookies.remove('franchise', { domain });
	Cookies.remove('sessionId', { domain });
	Cookies.remove(COOKIE_PLAYER_POINTS, { domain });
	Cookies.remove(COOKIE_PLAYER_BALANCE, { domain });
	setCookie(COOKIE_PLAYER_CASINO_NET_POSITION, '');
	removeSessionTime();
}

async function logout() {
	const response = await logoutCall();
	const { status, errorDescription, errorCode } = response;

	if (status === 'FAILURE') {
		postLogoutFail({ errorDescription, errorCode });
	}

	postLogout();

	return response;
}

async function getToken(payload) {
	const response = await getTokenCall(payload);

	if (!response.error) {
		storage.set('registerBasic', {
			registrationToken: response.value
		});
	}

	return response;
}

let cachedRegisterBoxResponse;

async function getLocaleInfo() {
	if (!cachedRegisterBoxResponse) {
		cachedRegisterBoxResponse = await getLocaleInfoCall();
	}

	if (cachedRegisterBoxResponse?.status === 'SUCCESS') {
		const {
			countries,
			currencies,
			dkGamblingRange: dkGamblingRangeId,
			callingcode,
			disablecountrycallingcode: { value: disableCountryCallingCode },
			states,
			nationalities,
			fields,
			occupations,
			industries,
			cities
		} = cachedRegisterBoxResponse.result.map;

		let callingCode = '';
		if (callingcode && callingcode.integer) {
			callingCode = callingcode.integer;
		}

		const selectedCountry = countries.list.find(({ selected }) => selected);

		const selectedCurrency = currencies.list.find(({ selected }) => selected);

		const selectedNationality = nationalities.list.find(({ selected }) => selected);
		const selectedState = states.list.find(({ selected }) => selected);

		return {
			nationality: {
				value: selectedNationality && selectedNationality.countryCode,
				options: nationalities?.list?.map(({ countryCode: value, text: label }) => ({
					value,
					label
				}))
			},
			occupation: {
				options: occupations?.list?.map(({ id, occupation: value, text: label }) => ({
					id,
					value,
					label,
					...(id !== undefined && { id }) // A check for ontario new register flow
				}))
			},
			industry: {
				options: industries?.list?.map(({ industry: value, text: label, occupationIds }) => ({
					value,
					label,
					...(occupationIds && Array.isArray(occupationIds) && { occupationIds }) // A check for ontario new register flow
				}))
			},
			city: {
				options: cities?.list?.map(({ city: value, text: label }) => ({
					value,
					label
				}))
			},
			country: {
				value: selectedCountry && selectedCountry.countryCode,
				options: countries?.list?.map(({ countryCode: value, text: label }) => ({
					value,
					label
				}))
			},
			state: {
				value: selectedState && selectedState.stateCode,
				options: states?.list?.map(({ stateCode: value, text: label, name }) => ({
					value,
					label,
					name
				}))
			},
			currency: {
				value: selectedCurrency && selectedCurrency.text,
				options: currencies.list.map(({ text }) => ({
					value: text,
					label: text
				}))
			},
			...(dkGamblingRangeId
				? {
						dkGamblingRangeId: {
							options: dkGamblingRangeId.result.list.map(({ gamblingRange, id }) => ({
								value: id,
								label: gamblingRange
							}))
						}
					}
				: {}),
			callingCode,
			disableCountryCallingCode,
			fields
		};
	}

	const { errorCode, errorDescription } = cachedRegisterBoxResponse;
	// reset cache on error
	cachedRegisterBoxResponse = undefined;
	return { error: { errorCode, errorDescription } };
}

function postSignupSuccess(payload, result, sourceFranchise) {
	if (payload.type === REGISTRATION_TYPES.MOJEID) {
		registrationSuccess({
			method: 'moje id',
			encryptedPID: result.encryptedPlayerId ?? null
		});
	} else if (payload.type === REGISTRATION_TYPES.COMMFIDES) {
		registrationSuccess({
			method: 'SMS',
			encryptedPID: result.encryptedPlayerId ?? null
		});
	} else if (payload.type === REGISTRATION_TYPES.XSELL) {
		registrationSuccess({
			method: `B2B x-sell - ${sourceFranchise}`
		});
	} else if (payload.type === REGISTRATION_TYPES.TRANSACTION_LINK) {
		registrationSuccess({
			method: 'TransactionLink',
			encryptedPID: result.encryptedPlayerId ?? null
		});
	} else {
		// gtm event
		registrationSuccess({
			method: 'UsernamePw',
			encryptedPID: result.encryptedPlayerId ?? null
		});
	}
}

function postSignupFailure({ errorCode, errorDescription, result, payload, sourceFranchise }) {
	if (payload.type === REGISTRATION_TYPES.MOJEID) {
		registrationFailure({
			method: 'moje id',
			error: `${errorCode} - ${errorDescription}`
		});
	} else if (payload.type === REGISTRATION_TYPES.COMMFIDES) {
		registrationFailure({
			method: 'SMS',
			error: `SMS - ${errorCode} - ${errorDescription}`
		});
	} else if (payload.type === REGISTRATION_TYPES.XSELL) {
		registrationFailure({
			method: 'SMS',
			error: `B2B x-sell - ${sourceFranchise} - ${errorCode} - ${errorDescription}`
		});
	} else {
		// gtm event
		registrationFailure({
			method: 'UsernamePw',
			error: `${errorCode} - ${errorDescription}`
		});
	}

	return { error: { errorCode, errorDescription, result } };
}

async function register(payload, { xSellReferrer, sourceFranchise }) {
	const response = await registerCall(payload, xSellReferrer);

	const { status, errorCode, errorDescription, result } = response;
	if (status === 'SUCCESS') {
		postSignupSuccess(payload, result, sourceFranchise);
	}

	if (status === 'FAILURE') {
		postSignupFailure({ errorCode, errorDescription, result, payload, sourceFranchise });
	}

	return response;
}

async function reopenAccount(payload) {
	return reopenAccountCall(payload);
}

async function registrationPhoneVerification({ phoneNumber, code }) {
	return registrationPhoneVerificationCall({ phoneNumber, code });
}

async function registrationSMSResend({ phoneNumber }) {
	return registrationSMSResendCall({ phoneNumber });
}

const isSpecificPostAuth = (path = '') =>
	isCasinoGamePath(path) ||
	path.includes(ROUTE_ACCOUNT) ||
	path.includes(ROUTE_WE_SPIN) ||
	path.includes(ROUTE_PROMOTIONS);

function generatePostAuthLocation(location, smaller, transformPath, basePathname) {
	let postAuthLocation = toLocation(location);
	const postAuthPath = postAuthLocation?.pathname ?? postAuthLocation;
	// If current location is a game, set postauth location to current location
	if (isSpecificPostAuth(postAuthPath)) {
		// for specific paths like game, account settings, dont modify path
		return postAuthLocation;
	}

	if (!smaller) {
		const pathname = postAuthLocation?.pathname ?? basePathname;
		postAuthLocation = transformPath(ROUTE_ACCOUNT, {
			location: { pathname }
		});
	} else {
		postAuthLocation = `${basePathname}`;
	}

	return postAuthLocation;
}

const isAboveAgeLimit = ({ birthday, limit }) => {
	if (Object.prototype.toString.call(birthday) !== '[object Date]') {
		return false;
	}
	const today = new Date();
	let age = today.getFullYear() - birthday.getFullYear();
	const m = today.getMonth() - birthday.getMonth();
	if (m < 0 || (m === 0 && today.getDate() < birthday.getDate())) {
		age -= 1;
	}
	return age >= limit;
};

const updateMissingData = data => {
	return updateMissingDataCall(data);
};

/**
 * Updates missing data for a German player
 *
 * @async
 * @function updateMissingDataGermany
 * @param {Object} params
 * @param {string} params.playerIdString
 * @param {string} [params.nationalityCountryCode]
 * @param {string} [params.stateCode]
 * @param {string} [params.birthplace]
 * @param {string} [params.familyName]
 * @returns {Promise<{status: 'SUCCESS' | 'FAILURE', errorCode?: string, errorDescription: string}>}
 */
const updateMissingDataGermany = async ({
	playerIdString,
	nationalityCountryCode,
	stateCode,
	birthplace,
	familyName
}) => {
	const sendExtraDetails = nationalityCountryCode || familyName || birthplace;
	const result = await Promise.all([
		...(sendExtraDetails
			? [
					sendExtraDetailsCall({
						playerIdString,
						nationalityCountryCode,
						familyName,
						birthplace
					})
				]
			: []),
		...(stateCode
			? [
					senStateCodeCall({
						encryptedPlayerId: playerIdString,
						stateCode
					})
				]
			: [])
	]);

	const parsedStatus = result[1]
		? result[0].status === 'SUCCESS' && result[1].status === 'SUCCESS'
		: result[0].status === 'SUCCESS';

	return {
		status: parsedStatus ? 'SUCCESS' : 'FAILURE',
		errorCode: result[0]?.errorCode ?? result[1]?.errorCode,
		errorDescription: result[0]?.errorDescription ?? result[1]?.errorDescription
	};
};

/**
 * loginLugas method
 *
 * @param {Object} params
 * @param {string} params.playerIdString
 * @param {number} [params.lugasDepositLimit]
 * @returns {Promise<{status: string, errorDescription: string, errorCode: string, result: {sessionId: string}}>}
 */
const loginLugas = ({ playerIdString, lugasDepositLimit }) => {
	return loginLugasCall({ encryptedPlayerId: playerIdString, lugasDepositLimit });
};

const sendTrackEventCheckbox = (eventAction, eventLabel) => {
	insertTrackEvent({
		category: 'register',
		action: eventAction,
		label: eventLabel
	});
};

export {
	getGoogleClientId,
	login,
	logout,
	postAuthSuccess,
	postLogout,
	getToken,
	getLocaleInfo,
	register,
	reopenAccount,
	sendClientIdForTracking,
	setSessionTime,
	removeSessionTime,
	removeRealityCheckCookies,
	registrationPhoneVerification,
	registrationSMSResend,
	generatePostAuthLocation,
	isAboveAgeLimit,
	prelogin,
	setTermsAndConditions,
	updateMissingData,
	updateMissingDataGermany,
	loginLugas,
	sendTrackEventCheckbox,
	completeSession,
	setPrivacyPolicy
};
